import { useTokenProvider, useConfig, useLogger, useAuthorizer } from '@gplibraries/react/hooks';
import { useQuery } from '@tanstack/react-query';
import { fetchDealerInfo, fetchUserMask } from '@gplibraries/react/api';
import { useState, useEffect } from 'react';
/**
 * Fetch dealer info.
 **
 * @returns {FetchResult}
 */
export function useDealerInfo() {
    const logger = useLogger();
    const { dealerAccessApiScope, dealerAccessApiHost } = useConfig();
    const getToken = useTokenProvider({ scopes: [dealerAccessApiScope] });
    const [dealerNumber, setDealerNumber] = useState(null);
    // middleware token query
    const { data: token, isLoading: tokenLoading } = useQuery({
        enabled: Boolean(getToken),
        queryKey: ['middlewareToken'],
        queryFn: () => getToken()
    });
    const user = useAuthorizer(token?.accessTokenClaims);
    // Get use the dealer mask as the dealer number if the user is not a dealer, otherwise use their dealer number (gpm Username)
    const { isLoading: maskLoading, error: maskError, data: mask } = useQuery({
        enabled: Boolean(user) && !user?.isDealer(),
        queryKey: ['userMask', user?.get('id')],
        queryFn: () => getToken()
            .then(token => fetchUserMask(dealerAccessApiHost, token.accessToken, token?.accessTokenClaims?.oid))
            .then(res => res.json()),
        select: (result) => result.data
    });
    const { isPending: dealerInfoPending, isEnabled, error, data: dealerInfo } = useQuery({
        enabled: Boolean(dealerNumber && getToken),
        queryKey: ['dealerInfo', dealerNumber],
        queryFn: () => getToken()
            .then(token => fetchDealerInfo(dealerAccessApiHost, token.accessToken, dealerNumber))
            .then(res => res.json()),
        select: (result) => result.data
    });
    useEffect(() => {
        if (mask && !dealerNumber) {
            setDealerNumber(mask);
        }
        if (user?.isDealer() && !dealerNumber) {
            setDealerNumber(user?.get('gpmUsername'));
        }
    }, [mask, user]);
    if (maskError)
        logger.error(maskError, {
            source: 'useDealerInfo',
            mask,
        });
    const loading = maskLoading || tokenLoading || (dealerInfoPending && isEnabled);
    return { data: dealerInfo, loading, error };
}
